import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Wifi } from 'lucide-react';

/**
 * "Offline" banner.
 *
 * `navigator.onLine` is notoriously unreliable — Chrome/macOS can report the
 * browser as offline while it's perfectly connected, and the `online` event
 * doesn't always fire to clear it, so the old version got stuck showing the
 * scary "you're offline" bar. We now treat `navigator.onLine`/events only as a
 * *hint* and confirm real connectivity with an actual request to our API before
 * ever alarming the user — and poll to auto-recover.
 */
async function apiReachable(): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch('/api/health', { method: 'GET', cache: 'no-store', signal: ctrl.signal });
    clearTimeout(t);
    return res.ok;
  } catch {
    return false;
  }
}

export function OfflineBanner() {
  const [online, setOnline] = useState(true); // assume online; only alarm after a real check fails
  const [showReconnected, setShowReconnected] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const offlineRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const stopPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const goOnline = () => {
      if (offlineRef.current) {
        offlineRef.current = false;
        setShowReconnected(true);
      }
      setOnline(true);
      stopPoll();
    };

    const goOffline = () => {
      offlineRef.current = true;
      setOnline(false);
      if (!pollRef.current) {
        // Keep checking; the moment the API answers again, clear the banner —
        // don't depend on the flaky `online` event firing.
        pollRef.current = setInterval(async () => {
          if (await apiReachable()) {
            if (!cancelled) goOnline();
          }
        }, 5000);
      }
    };

    // A hint (event or navigator flag) triggers a *real* check before alarming.
    async function verify() {
      const ok = await apiReachable();
      if (cancelled) return;
      if (ok) goOnline();
      else goOffline();
    }

    window.addEventListener('online', verify);
    window.addEventListener('offline', verify);
    // On load, only bother checking if the browser claims to be offline.
    if (typeof navigator !== 'undefined' && !navigator.onLine) verify();

    return () => {
      cancelled = true;
      stopPoll();
      window.removeEventListener('online', verify);
      window.removeEventListener('offline', verify);
    };
  }, []);

  // Auto-dismiss the "reconnected" banner after 3s
  useEffect(() => {
    if (!showReconnected) return;
    const timer = setTimeout(() => setShowReconnected(false), 3000);
    return () => clearTimeout(timer);
  }, [showReconnected]);

  const showBanner = !online || showReconnected;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className={
            'fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium text-white ' +
            (online ? 'bg-emerald-500' : 'bg-amber-500')
          }
        >
          {online ? (
            <>
              <Wifi className="size-3.5" />
              Back online — you're all set
            </>
          ) : (
            <>
              <WifiOff className="size-3.5" />
              You're offline — changes won't be saved until you reconnect
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
